// Organizations tab — list + filters + pagination + detail drawer + create/delete.

import { useState } from "react";
import { SortableHeader } from "@/components/admin/SortableHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/ui/Loading";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";
import { useStepUp } from "@/context/StepUpContext";
import {
  useAdminOrgDetail,
  useAdminOrgs,
  useCreateOrg,
  useDeleteOrg,
} from "@/hooks/useAdmin";
import type { AdminOrgDetail } from "@/types/admin";
import { useDict, fmt } from "@/i18n/LocaleProvider";

const LIMIT = 50;
const GRID = "2fr 1fr 90px 110px 110px 80px";

// Plan badge colors — free stays muted gray; paid tiers get distinct,
// readable accents so an admin can scan plans at a glance.
const PLAN_BADGE: Record<string, { color: string; bg: string; border: string }> = {
  free: { color: "rgb(var(--color-muted))", bg: "rgb(var(--color-bg))", border: "rgb(var(--color-stroke))" },
  basic: { color: "rgb(var(--color-info))", bg: "rgb(var(--color-info) / 0.102)", border: "rgb(var(--color-info) / 0.333)" },
  pro: { color: "rgb(var(--color-accent))", bg: "rgb(var(--color-accent) / 0.102)", border: "rgb(var(--color-accent) / 0.4)" },
  enterprise: { color: "rgb(var(--color-brand))", bg: "rgb(var(--color-brand) / 0.102)", border: "rgb(var(--color-brand) / 0.333)" },
};

function planBadge(plan: string | null | undefined) {
  return PLAN_BADGE[(plan || "free").toLowerCase()] ?? PLAN_BADGE.free;
}

function formatErr(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function AdminOrgsTab() {
  const dd = useDict();
  const t = dd.app.adminOrgs;
  const est = dd.app.engagements.statuses;
  const roleMap = dd.app.members.roles;
  const { withStepUp } = useStepUp();

  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  function onSort(column: string) {
    if (sortBy === column) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setOrder("asc");
    }
    setOffset(0);
  }

  const orgs = useAdminOrgs({
    q: q || undefined,
    sort_by: sortBy,
    order,
    limit: LIMIT,
    offset,
  });
  const selected = useAdminOrgDetail(selectedId);
  const createOrg = useCreateOrg();
  const deleteOrg = useDeleteOrg();

  function handleCreate() {
    if (!newName.trim()) return;
    withStepUp(async (token) => {
      try {
        await createOrg.mutateAsync({
          name: newName,
          description: newDesc,
          stepUpToken: token,
        });
        toast.success(t.orgCreated);
        setShowCreate(false);
        setNewName("");
        setNewDesc("");
      } catch (e) {
        toast.error(`${t.createFailed} ${formatErr(e)}`);
      }
    });
  }

  async function handleDelete(o: AdminOrgDetail, force: boolean) {
    const ok = await confirm({
      title: force ? t.forceDeleteTitle : t.deleteTitle,
      message: force
        ? fmt(t.forceDeleteMsg, { name: o.name, n: o.engagement_count ?? 0 })
        : fmt(t.deleteMsg, { name: o.name }),
      variant: "danger",
      confirmLabel: force ? t.forceDelete : t.delete,
    });
    if (!ok) return;
    withStepUp(async (token) => {
      try {
        await deleteOrg.mutateAsync({
          orgId: o.id,
          force,
          stepUpToken: token,
        });
        toast.success(t.orgDeleted);
        setSelectedId(null);
      } catch (e) {
        toast.error(`${t.deleteFailed} ${formatErr(e)}`);
      }
    });
  }

  if (orgs.isError) {
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[11px]">
          {formatErr(orgs.error)}
        </div>
      </Card>
    );
  }

  const d = orgs.data;

  return (
    <div>
      {/* Filters + create button */}
      <Card className="mb-3 !px-[14px] !py-3">
        <div className="flex gap-[10px] items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
            placeholder={t.searchPlaceholder}
            className="flex-1 px-[10px] py-[7px] bg-bg border border-stroke rounded text-content font-mono text-[11px] outline-none"
          />
          {d && (
            <div className="font-mono text-[10px] text-muted">
              {fmt(t.orgsCount, { n: d.total ?? 0 })}
            </div>
          )}
          <Button
            variant="primary"
            small
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? t.cancel : t.newOrg}
          </Button>
        </div>
      </Card>

      {/* Create form */}
      {showCreate && (
        <Card className="mb-3 border-accent">
          <div className="font-mono text-[11px] text-accent uppercase tracking-[0.08em] mb-3">
            {t.createTitle}
          </div>
          <Input
            label={t.nameLabel}
            value={newName}
            onChange={setNewName}
            placeholder={t.namePlaceholder}
          />
          <Input
            label={t.descLabel}
            value={newDesc}
            onChange={setNewDesc}
            textarea
          />
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!newName.trim()}
          >
            {t.createBtn}
          </Button>
        </Card>
      )}

      {/* Orgs list — desktop grid table from md+, stacked cards below. */}
      {!d ? (
        <Loading text={t.loading} />
      ) : d.orgs.length === 0 ? (
        <Card>
          <div className="text-dim text-center py-6 font-mono text-[11px]">
            {t.noOrgs}
          </div>
        </Card>
      ) : (
        <>
          {/* Mobile: stacked cards */}
          <div className="md:hidden flex flex-col gap-3">
            {d.orgs.map((o) => (
              <Card
                key={o.id}
                onClick={() => setSelectedId(o.id)}
                className="!p-4"
              >
                <div className="flex justify-between items-start gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-sans text-[14px] font-semibold text-content truncate">
                      {o.name}
                    </div>
                    {o.description && (
                      <div className="font-mono text-[10px] text-muted truncate">
                        {o.description}
                      </div>
                    )}
                  </div>
                  <span
                    className="px-2 py-[3px] rounded-[3px] text-[9px] uppercase shrink-0 font-bold"
                    style={{
                      background: planBadge(o.plan).bg,
                      border: `1px solid ${planBadge(o.plan).border}`,
                      color: planBadge(o.plan).color,
                    }}
                  >
                    {o.plan}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-muted">
                  <span>{o.member_count ?? 0} {t.membersLabel}</span>
                  <span
                    style={{
                      color:
                        (o.engagement_count ?? 0) > 0 ? "rgb(var(--color-accent))" : "rgb(var(--color-muted))",
                    }}
                  >
                    {o.engagement_count ?? 0} {t.engagementsLabel}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop: grid table */}
          <Card className="!p-0 overflow-hidden hidden md:block">
            <div
              className="grid gap-3 px-4 py-3 bg-bg2 border-b border-stroke font-mono text-[10px] text-muted uppercase tracking-[0.05em] font-semibold items-center"
              style={{ gridTemplateColumns: GRID }}
            >
              <SortableHeader label={t.colName} column="name" active={sortBy} order={order} onSort={onSort} />
              <div>{t.colUUID}</div>
              <SortableHeader label={t.colPlan} column="plan" active={sortBy} order={order} onSort={onSort} />
              <SortableHeader label={t.colMembers} column="member_count" active={sortBy} order={order} onSort={onSort} />
              <SortableHeader label={t.colEngagements} column="engagement_count" active={sortBy} order={order} onSort={onSort} />
              <div />
            </div>
            {d.orgs.map((o) => (
              <div
                key={o.id}
                onClick={() => setSelectedId(o.id)}
                className="grid gap-3 px-4 py-3 border-b border-stroke font-mono text-[11px] items-center cursor-pointer"
                style={{ gridTemplateColumns: GRID }}
              >
                <div>
                  <div className="text-content font-semibold">{o.name}</div>
                  {o.description && (
                    <div className="text-muted text-[9px] mt-[2px] truncate">
                      {o.description}
                    </div>
                  )}
                </div>
                <div className="text-muted text-[10px] font-mono truncate" title={o.id}>{o.id}</div>
                <div>
                  <span
                    className="px-[6px] py-[2px] rounded-[3px] text-[9px] uppercase font-bold"
                    style={{
                      background: planBadge(o.plan).bg,
                      border: `1px solid ${planBadge(o.plan).border}`,
                      color: planBadge(o.plan).color,
                    }}
                  >
                    {o.plan}
                  </span>
                </div>
                <div className="text-content">{o.member_count ?? 0}</div>
                <div
                  style={{
                    color:
                      (o.engagement_count ?? 0) > 0 ? "rgb(var(--color-accent))" : "rgb(var(--color-muted))",
                  }}
                >
                  {o.engagement_count ?? 0}
                </div>
                <div className="text-right">
                  <span className="text-accent text-[10px]">{t.detail}</span>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {/* Pagination */}
      {d && d.total > LIMIT && (
        <div className="flex justify-center gap-2 mt-3">
          <Button
            variant="default"
            small
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            {t.prev}
          </Button>
          <span className="font-mono text-[11px] text-muted self-center">
            {offset + 1}-{Math.min(offset + LIMIT, d.total)} {t.rangeOf} {d.total}
          </span>
          <Button
            variant="default"
            small
            disabled={offset + LIMIT >= d.total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            {t.next}
          </Button>
        </div>
      )}

      {/* Detail drawer — full-width on mobile, fixed 500px panel from md+ */}
      {selectedId != null && selected.data && (
        <div
          className="fixed inset-0 md:top-0 md:right-0 md:bottom-0 md:left-auto md:inset-y-0 md:w-[500px] overflow-auto"
          style={{
            background: "rgb(var(--color-bg2))",
            borderLeft: "2px solid rgb(var(--color-accent))",
            zIndex: 900,
            boxShadow: "-4px 0 16px rgba(0,0,0,0.5)",
          }}
        >
          <div className="p-5 border-b border-stroke flex justify-between items-center">
            <div>
              <div className="font-display text-[16px] font-bold text-content">
                {selected.data.name}
              </div>
              <div className="font-mono text-[11px] text-muted mt-[2px]">
                {selected.data.slug} · {selected.data.plan}
              </div>
            </div>
            <button
              onClick={() => setSelectedId(null)}
              className="bg-transparent border-none text-muted text-2xl cursor-pointer px-2"
            >
              ×
            </button>
          </div>
          <div className="p-5">
            {selected.data.description && (
              <div
                className="p-[10px] rounded font-mono text-[11px] text-muted mb-4"
                style={{ background: "rgb(var(--color-bg))", border: "1px solid rgb(var(--color-stroke))" }}
              >
                {selected.data.description}
              </div>
            )}

            <div className="grid grid-cols-2 gap-[10px] mb-4">
              <div
                className="p-[10px] rounded"
                style={{
                  background: "rgb(var(--color-bg))",
                  border: "1px solid rgb(var(--color-stroke))",
                }}
              >
                <div className="font-mono text-[9px] text-dim uppercase mb-[3px]">
                  {t.membersStat}
                </div>
                <div className="font-display text-[20px] font-bold text-content">
                  {selected.data.member_count ?? 0}
                </div>
              </div>
              <div
                className="p-[10px] rounded"
                style={{
                  background: "rgb(var(--color-bg))",
                  border: "1px solid rgb(var(--color-stroke))",
                }}
              >
                <div className="font-mono text-[9px] text-dim uppercase mb-[3px]">
                  {t.engagementsStat}
                </div>
                <div className="font-display text-[20px] font-bold text-content">
                  {selected.data.total_engagements ?? 0}
                </div>
              </div>
            </div>

            {selected.data.engagement_count_by_status &&
              Object.keys(selected.data.engagement_count_by_status).length >
                0 && (
                <div className="mb-4">
                  <div className="font-mono text-[9px] text-muted uppercase mb-[6px]">
                    {t.byStatus}
                  </div>
                  {Object.entries(selected.data.engagement_count_by_status).map(
                    ([st, c]) => (
                      <div
                        key={st}
                        className="flex justify-between px-[10px] py-1 rounded mb-[3px] font-mono text-[10px]"
                        style={{
                          background: "rgb(var(--color-bg))",
                          border: "1px solid rgb(var(--color-stroke))",
                        }}
                      >
                        <span className="text-muted capitalize">
                          {est[st as keyof typeof est] ?? st.replace("_", " ")}
                        </span>
                        <span className="text-content font-semibold">
                          {c}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              )}

            {selected.data.members && selected.data.members.length > 0 && (
              <div className="mb-4">
                <div className="font-mono text-[9px] text-muted uppercase mb-[6px]">
                  {fmt(t.membersCount, { n: selected.data.members.length })}
                </div>
                <div
                  className="max-h-[200px] overflow-y-auto rounded"
                  style={{ border: "1px solid rgb(var(--color-stroke))" }}
                >
                  {selected.data.members.map((m) => (
                    <div
                      key={m.user_id}
                      className="flex justify-between px-[10px] py-2 border-b border-stroke font-mono text-[10px]"
                    >
                      <div>
                        <div className="text-content font-semibold">
                          {m.full_name || m.email}
                        </div>
                        <div className="text-muted text-[9px]">{m.email}</div>
                      </div>
                      <span
                        className="px-[6px] py-[2px] rounded-[3px] text-[8px] font-bold uppercase self-center"
                        style={{
                          background: "rgb(var(--color-accent-bg))",
                          border: "1px solid rgb(var(--color-accent) / 0.4)",
                          color: "rgb(var(--color-accent))",
                        }}
                      >
                        {roleMap[m.role as keyof typeof roleMap] ?? m.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <div className="font-mono text-[9px] text-muted uppercase mb-[2px]">
                {t.actionsStepUp}
              </div>
              <Button
                variant="default"
                full
                disabled={(selected.data.total_engagements ?? 0) > 0}
                onClick={() => handleDelete(selected.data!, false)}
              >
                {t.deleteSafe}
              </Button>
              <Button
                variant="danger"
                full
                onClick={() => handleDelete(selected.data!, true)}
              >
                {t.forceDeleteCascade}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
